import React, { useRef } from "react";
import z from "zod";

import { AiSettingsSchema } from "@performyard/models";
import { FormHandle, PYForm, VALIDATE_ON, toast } from "@performyard/py-components";

import {
	CheckboxGroup,
	SectionLayout,
	SettingsFormFooter,
	SettingsToggle,
	UnsavedChangesTracker,
} from "../../components";

import { useAiSettings, useUpdateAiSettings } from "../../../../../api/hooks";
import useUnsavedChanges from "../../../../../hooks/useUnsavedChanges";
import { useAiSettingsDefaults } from "../../hooks";

const AIInsightsSurveysSettingsTab = () => {
	const formRef = useRef<FormHandle<typeof AiSettingsSchema.shape.surveyInsightsSettings>>(null);

	// AI settings data management
	const { mutateAsync: updateAiSettings, isPending } = useUpdateAiSettings();
	const { data, isLoading } = useAiSettings();

	// Unsaved changes tracking
	const { setIsUnsafe, setIsSafe } = useUnsavedChanges();

	// toggles
	const isSurveyInsightsAssistantEnabled = data?.surveyInsightsEnabled ?? true;

	// toggle handlers
	const handleSurveyInsightsAssistantToggle = async (val: boolean) => {
		await updateAiSettings({ surveyInsightsEnabled: val });
		toast.success(`Survey Insights Assistant ${val === true ? "Enabled" : "Disabled"}.`);
	};

	// Handle form submission and save changes to AI settings
	const handleSave = async ({
		value,
		resetForm,
	}: {
		value: z.infer<typeof AiSettingsSchema.shape.surveyInsightsSettings>;
		resetForm: () => void;
	}) => {
		try {
			await updateAiSettings({ surveyInsightsSettings: value });
			toast.success("Your changes have been saved.");

			// Reset the form normally - it will use the updated defaultValues from the cache
			resetForm();

			// Mark form as safe after successful save
			setIsSafe();
		} catch {
			toast.error("Unable to save settings.");
		}
	};

	// Checkbox options
	const checkBoxOptions = [
		{ value: "sentimentAnalysis", label: "Sentiment Analysis", dataTestId: "sentiment-analysis-checkbox" },
		{
			value: "keyThemesAndPatterns",
			label: "Key Themes & Patterns",
			dataTestId: "key-themes-and-patterns-checkbox",
		},
		{
			value: "outliersAndNotableResponses",
			label: "Outliers & Notable Responses",
			dataTestId: "outliers-and-notable-responses-checkbox",
		},
		{
			value: "actionableInsightsAndRecommendations",
			label: "Actionable Insights & Recommendations",
			dataTestId: "actionable-insights-and-recommendations-checkbox",
		},
		{ value: "demographicInsights", label: "Demographic Insights", dataTestId: "demographic-insights-checkbox" },
	];

	const defaultValues = useAiSettingsDefaults({
		schema: AiSettingsSchema.shape.surveyInsightsSettings,
		data,
		extractDefaults: (allDefaults) => allDefaults.surveyInsightsSettings,
		extractData: (data) => data.surveyInsightsSettings,
	});

	if (isLoading) {
		return null;
	}

	return (
		<PYForm
			ref={formRef}
			id="ai-insights-surveys-settings-form"
			validateOn={[VALIDATE_ON.ON_SUBMIT]}
			schema={AiSettingsSchema.shape.surveyInsightsSettings}
			defaultValues={defaultValues}
			onSubmit={handleSave}
		>
			<UnsavedChangesTracker
				id="ai-insights-surveys-unsaved-changes"
				setIsUnsafe={setIsUnsafe}
				setIsSafe={setIsSafe}
			/>
			<SectionLayout
				key="surveys-settings-section"
				title="Survey Insights Assistant"
				description="The Survey Insights Assistant allows admins to generate insights about survey participation responses received."
			>
				<SettingsToggle
					key="survey-insights-toggle"
					id="enable-survey-insights-assistant"
					label="Enable Survey Insights Assistant"
					description="Disabling this setting will hide the ability for admins to generate insights for a survey."
					checked={isSurveyInsightsAssistantEnabled}
					onCheckedChange={handleSurveyInsightsAssistantToggle}
					dataTestId="survey-insights-assistant-toggle"
				/>

				<CheckboxGroup
					key="surveys-insights-content-checkboxes"
					title="Insights Content"
					description="Control each section of content within your survey insights, to only highlight content that is relevant to you and your company. These options are in addition to an overall summary of the survey."
					options={checkBoxOptions}
				/>
			</SectionLayout>

			<SettingsFormFooter
				key="surveys-settings-footer"
				formId="ai-insights-surveys-settings-form"
				isPending={isPending}
			/>
		</PYForm>
	);
};

export default AIInsightsSurveysSettingsTab;
